import { useEffect, useState } from "react";
import operatorApiService from "../../../services/operator-api.service";
import "../CommonDesign.scss";
import editIcon from "../../../assets/imgs/master/edit_icon_wh.svg";
import deleteIcon from "../../../assets/imgs/master/delete_icon_wh.svg";

interface EditableAnnouncement {
  announcement_cd: string;
  base_id: number;
  announcement: string;
  display_number: number;
  next_announcement_cd: string | null;
  next_playback_spk: number | null;
  priority: string;
  volume: string;
  announcement_count: string;
  is_screenless_start: number;
  status: "original" | "edited" | "new" | "deleted";
  isEditing?: boolean;
  internalId: string;
  original_cd?: string;
}

interface AnnouncementProps {
  baseId: number | null;
}

const Announcement = ({ baseId }: AnnouncementProps) => {
  const [rows, setRows] = useState<EditableAnnouncement[]>([]);
  const [originalRows, setOriginalRows] = useState<EditableAnnouncement[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!baseId) return;
    const fetchData = async () => {
      const res = await operatorApiService.doGetAnnouncements({ baseId });
      const mapped = res.map((a: any) => ({
        ...a,
        status: "original",
        isEditing: false,
        internalId: `existing-${a.announcement_cd}`,
      }));

      setRows(mapped);
      setOriginalRows(mapped);
    };
    fetchData();
  }, [baseId]);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        announcement_cd: "",
        base_id: baseId!,
        announcement: "",
        display_number: 0,
        next_announcement_cd: "",
        next_playback_spk: null,
        priority: "0",
        volume: "0",
        announcement_count: "0",
        is_screenless_start: 0,
        status: "new",
        isEditing: true,
        internalId: `new-${Date.now()}-${Math.random()}`,
      },
    ]);
  };

  const handleChange = (
    index: number,
    field: keyof EditableAnnouncement,
    value: any
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;
    setRows(updated);
  };

  const toggleEdit = (internalId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.internalId === internalId
          ? {
              ...r,
              isEditing: true,
              status: "edited",
              original_cd: r.announcement_cd,
            }
          : r
      )
    );
  };

  const markDeleted = (internalId: string) => {
    setRows((prev) =>
      prev
        .filter((r) => {
          if (r.internalId !== internalId) return true;
          return r.status !== "new";
        })
        .map((r) =>
          r.internalId === internalId && r.status !== "new"
            ? { ...r, status: "deleted" }
            : r
        )
    );
  };

  const handleReset = () => {
    setRows(originalRows.map((r) => ({ ...r })));
  };

  const submit = async () => {
    try {
      const hasBlank = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (r.announcement_cd.trim() === "" ||
            r.announcement.trim() === "" ||
            r.display_number === null ||
            r.priority.trim() === "" ||
            r.volume.trim() === "" ||
            r.announcement_count.trim() === "" ||
            r.is_screenless_start === null)
      );

      if (hasBlank) {
        setErrorMessage("空欄があります。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const hasTooLong = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (r.announcement_cd.trim().length > 4 ||
            r.announcement.trim().length > 30 ||
            (r.next_announcement_cd ?? "").trim().length > 4 ||
            r.priority.trim().length > 1 ||
            r.volume.trim().length > 1 ||
            r.announcement_count.trim().length > 2)
      );

      if (hasTooLong) {
        setErrorMessage("文字数が制限を超えています。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const hasInvalidNumber = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (isNaN(Number(r.display_number)) ||
            isNaN(Number(r.next_playback_spk)) ||
            isNaN(Number(r.is_screenless_start)))
      );

      if (hasInvalidNumber) {
        setErrorMessage("数値でない項目があります。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const create = rows.filter((r) => r.status === "new");
      const update = rows
        .filter((r) => r.status === "edited")
        .map((r) => ({ ...r, original_cd: r.original_cd }));
      const del = rows
        .filter((r) => r.status === "deleted")
        .map((r) => r.announcement_cd);

      const response = await operatorApiService.saveAnnouncements({
        baseId,
        create,
        update,
        delete: del,
      });

      if (response.error === "duplicate") {
        setErrorMessage("指定された固定音声CDは既に使用されています。");
        setTimeout(() => setErrorMessage(null), 3000);
      }

      if (response.status === "success") {
        setSuccessMessage("更新に成功しました。");
        setTimeout(() => setSuccessMessage(null), 3000);
        const refreshed = await operatorApiService.doGetAnnouncements({
          baseId,
        });
        const mapped = refreshed.map((a: any) => ({
          ...a,
          status: "original",
          isEditing: false,
          internalId: `existing-${a.announcement_cd}`,
        }));
        setRows(mapped);
        setOriginalRows(mapped);
      }
    } catch (error) {
      setErrorMessage("更新に失敗しました。");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  return (
    <div className="announcement">
      <div className="content">
        <div className="title">固定音声</div>
        <div className="section">
          <div className="sectionTitle">固定音声一覧</div>
          <div className="sectionBody">
            <table className="tableArea">
              <thead>
                <tr>
                  <th>固定音声CD</th>
                  <th>固定音声名称</th>
                  <th>画面表示順</th>
                  <th>次再生固定音声CD</th>
                  <th>次再生スピーカー</th>
                  <th>優先順位</th>
                  <th>音量</th>
                  <th>再生回数</th>
                  <th>画面指示不要開始フラグ</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.internalId}>
                    <td
                      className={`littleWideCol ${
                        row.status === "deleted" ? "deleteCell" : ""
                      }`}
                    >
                      {row.isEditing ? (
                        <input
                          type="text"
                          value={row.announcement_cd}
                          onChange={(e) =>
                            handleChange(i, "announcement_cd", e.target.value)
                          }
                        />
                      ) : (
                        row.announcement_cd
                      )}
                    </td>

                    <td
                      className={`wideCol ${
                        row.status === "deleted" ? "deleteCell" : ""
                      }`}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.announcement}
                          onChange={(e) =>
                            handleChange(i, "announcement", e.target.value)
                          }
                        />
                      ) : (
                        row.announcement
                      )}
                    </td>

                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.display_number}
                          onChange={(e) =>
                            handleChange(i, "display_number", e.target.value)
                          }
                        />
                      ) : (
                        row.display_number
                      )}
                    </td>

                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.next_announcement_cd ?? ""}
                          onChange={(e) =>
                            handleChange(
                              i,
                              "next_announcement_cd",
                              e.target.value
                            )
                          }
                        />
                      ) : (
                        row.next_announcement_cd
                      )}
                    </td>

                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.next_playback_spk ?? ""}
                          onChange={(e) =>
                            handleChange(i, "next_playback_spk", e.target.value)
                          }
                        />
                      ) : (
                        row.next_playback_spk
                      )}
                    </td>

                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.priority}
                          onChange={(e) =>
                            handleChange(i, "priority", e.target.value)
                          }
                        />
                      ) : (
                        row.priority
                      )}
                    </td>

                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.volume}
                          onChange={(e) =>
                            handleChange(i, "volume", e.target.value)
                          }
                        />
                      ) : (
                        row.volume
                      )}
                    </td>

                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.announcement_count}
                          onChange={(e) =>
                            handleChange(
                              i,
                              "announcement_count",
                              e.target.value
                            )
                          }
                        />
                      ) : (
                        row.announcement_count
                      )}
                    </td>

                    <td
                      className={`wideCol ${
                        row.status === "deleted" ? "deleteCell" : ""
                      }`}
                    >
                      {row.isEditing ? (
                        <select
                          value={row.is_screenless_start}
                          onChange={(e) =>
                            handleChange(
                              i,
                              "is_screenless_start",
                              e.target.value
                            )
                          }
                        >
                          <option value={0}>OFF</option>
                          <option value={1}>ON</option>
                        </select>
                      ) : row.is_screenless_start === 1 ? (
                        "ON"
                      ) : (
                        "OFF"
                      )}
                    </td>

                    <td>
                      {row.status === "deleted" ? (
                        <span className="deletionLabel">削除予定</span>
                      ) : row.status === "edited" ? (
                        <span className="editLabel">編集予定</span>
                      ) : row.status === "new" ? (
                        <button
                          className="cancelButton"
                          onClick={() => markDeleted(row.internalId)}
                        >
                          取消
                        </button>
                      ) : (
                        <div className="buttonGroup">
                          <button
                            className="updateButton"
                            onClick={() => toggleEdit(row.internalId)}
                          >
                            <img src={editIcon} alt="" className="icon" />
                            編集
                          </button>
                          <button
                            className="deleteButton"
                            onClick={() => markDeleted(row.internalId)}
                          >
                            <img src={deleteIcon} alt="" className="icon" />
                            削除
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="createFooter">
              <div className="createLabel">
                表に新規入力欄が追加されます。
                新規固定音声を入力し、登録してください。
              </div>
              <button className="createButton" onClick={addRow}>
                新規行追加
              </button>
            </div>
            {successMessage && (
              <div className="successMessage">{successMessage}</div>
            )}
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
            {rows.some((r) => r.status !== "original") && (
              <div className="actionButtons">
                <button className="backButton" onClick={handleReset}>
                  戻る
                </button>
                <button className="decisionButton" onClick={submit}>
                  確定
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Announcement;
